import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';

import { AttackSurfaceSelector } from '@/components/AttackSurfaceSelector';
import { Input } from '@/components/form/Input';
import { StyledDatePicker } from '@/components/form/StyledDatePicker';
import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { Container } from '@/components/ui';
import { useGetMonitoringCounts } from '@/hooks/useGetMonitoringStats';
import { useMSRPCalculator } from '@/hooks/useMSRPCalculator';
import {
  useGetSubscription,
  useUpdateSubscription,
} from '@/hooks/useSubscription';
import {
  NegotiatedPriceInput,
  ScanningFrequencySelector,
  SubscriptionTierSelector,
} from '@/sections/settings/subscription/components';
import {
  MSRPPriceSection,
  UsagePriceSection,
} from '@/sections/settings/subscription/configure/components';
import {
  ScanFrequency,
  SUBSCRIPTION_INPUT_CLASS,
} from '@/sections/settings/subscription/constant';
import { useAuth } from '@/state/auth';
import { SubscriptionConfiguration } from '@/types';
import { cn } from '@/utils/classname';
import { calculateAssetTotal } from '@/utils/monitoring';

interface ConfigureSubscriptionProps {
  open: boolean;
  onClose: () => void;
  customerEmail?: string;
}

export function ConfigureSubscription({
  open,
  onClose,
  customerEmail,
}: ConfigureSubscriptionProps) {
  // State management with default values
  const { isPraetorianUser } = useAuth();

  const [localSubscription, setLocalSubscription] =
    useState<SubscriptionConfiguration>({
      estimatedCost: 0,
      attackSurface: {
        external: false,
        applications: false,
        cloud: false,
        code: false,
        internal: false,
        users: false,
        device: false,
      },
      numberOfAssets: 0,
      numberOfApplications: 0,
      scanSchedule: 'daily',
      selectedTier: 'professional',
      startDate: '',
      endDate: '',
    });

  const { data: subscriptionValue, status: subscriptionStatus } =
    useGetSubscription({ email: customerEmail });

  const { mutateAsync: updateSubscription, status: updateSubscriptionStatus } =
    useUpdateSubscription({ email: customerEmail });

  // Get monitoring counts for actual asset and application totals
  const { data: monitoringCounts } = useGetMonitoringCounts({
    email: customerEmail,
  });

  // Calculate total assets from monitoring data (React 19: simple transformations don't need useMemo)
  const actualTotalAssets = !monitoringCounts
    ? 0
    : calculateAssetTotal(monitoringCounts, localSubscription.attackSurface);

  // React 19: Simple property access doesn't need useMemo
  const actualTotalApplications = monitoringCounts?.applications?.webseed || 0;

  // Calculate Usage Price based on actual current usage
  const { floor: usageFloor, msrp: usagePrice } = useMSRPCalculator({
    numberOfApplications: actualTotalApplications,
    scanSchedule: localSubscription.scanSchedule,
    numberOfAssets: actualTotalAssets,
    selectedTier: localSubscription.selectedTier,
  });

  // Calculate MSRP based on coverage limits
  const { floor, msrp, breakdown, floorBreakdown } = useMSRPCalculator({
    numberOfApplications: localSubscription.numberOfApplications,
    scanSchedule: localSubscription.scanSchedule,
    numberOfAssets: localSubscription.numberOfAssets,
    selectedTier: localSubscription.selectedTier,
  });

  const handleSubscriptionChange = useCallback(
    (value: Partial<SubscriptionConfiguration>) => {
      setLocalSubscription(prev => ({ ...prev, ...value }));
    },
    []
  );

  const handleAttackSurfaceChange = useCallback(
    (
      newAttackSurface: import('@/components/AttackSurfaceSelector').AttackSurfaceSelection
    ) => {
      setLocalSubscription(prev => ({
        ...prev,
        // Convert AttackSurfaceSelection to Record<string, boolean> safely
        attackSurface: { ...newAttackSurface } as Record<string, boolean>,
      }));
    },
    []
  );

  const handleAttackSurfaceSelectionChange = useCallback(
    (
      key: keyof import('@/components/AttackSurfaceSelector').AttackSurfaceSelection,
      selected: boolean
    ) => {
      // When Web Applications is disabled, reset the number of applications
      if (key === 'applications' && !selected) {
        handleSubscriptionChange({ numberOfApplications: 0 });
      }
    },
    [handleSubscriptionChange]
  );

  // Memoized save handler
  const handleSave = useCallback(async () => {
    // Validate Web FQDNs when web attack surface is enabled
    if (
      localSubscription.attackSurface.applications &&
      localSubscription.numberOfApplications === 0
    ) {
      toast.error(
        '# of Web FQDNs cannot be 0 when Web attack surface is enabled'
      );
      return;
    }
    // Include MSRP in saved configuration if Praetorian user
    const configToSave = isPraetorianUser
      ? {
          ...localSubscription,
          msrp,
          msrpBreakdown: breakdown,
          floorPrice: floor,
          floorBreakdown,
        }
      : localSubscription;
    await updateSubscription(configToSave);
    onClose();
  }, [
    localSubscription,
    updateSubscription,
    onClose,
    isPraetorianUser,
    msrp,
    breakdown,
    floor,
    floorBreakdown,
  ]);

  // Sync local subscription state when server data changes
  // Using subscriptionStatus as a stable trigger - when status changes from loading to success,
  // we know we have fresh data. This avoids JSON.stringify which breaks memoization.
  useEffect(() => {
    if (subscriptionValue && subscriptionStatus === 'success') {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Syncing server data to local state on fetch success; useEffect is the correct pattern for external data sync
      setLocalSubscription(_prev => ({
        ...subscriptionValue,
        attackSurface: {
          external: subscriptionValue.attackSurface?.external ?? false,
          applications: subscriptionValue.attackSurface?.applications ?? false,
          cloud: subscriptionValue.attackSurface?.cloud ?? false,
          code: subscriptionValue.attackSurface?.code ?? false,
          internal: subscriptionValue.attackSurface?.internal ?? false,
          users: subscriptionValue.attackSurface?.users ?? false,
          device: subscriptionValue.attackSurface?.device ?? false,
        },
        startDate: subscriptionValue.startDate || '',
        endDate: subscriptionValue.endDate || '',
      }));
    }
  }, [subscriptionStatus, subscriptionValue]);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Configure Subscription"
      size="6xl"
      baseClassName="max-h-[85vh]"
      disableAutoFocus={true}
      footer={{
        isLoading: updateSubscriptionStatus === 'pending',
        text: 'Save',
        className: 'w-fit',
        onClick: handleSave,
      }}
      data-testid="configure-subscription-modal"
      aria-labelledby="configure-subscription-title"
    >
      <Loader
        type="spinner"
        isLoading={subscriptionStatus === 'pending' && !subscriptionValue}
      >
        <div
          className="space-y-3"
          role="form"
          aria-label="Subscription configuration form"
        >
          {/* Subscription Term + Attack Surface Coverage Row */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {/* Subscription Term Section */}
            <section className="space-y-2" aria-labelledby="term-section-title">
              <h3
                id="term-section-title"
                className="text-chariot-text-primary text-lg font-semibold"
              >
                Subscription Term
              </h3>
              <div className="flex items-center gap-3">
                <label className="text-chariot-text-primary shrink-0 text-xs">
                  Start
                </label>
                <StyledDatePicker
                  label=""
                  name="start-date"
                  value={localSubscription.startDate || ''}
                  onChange={value =>
                    handleSubscriptionChange({ startDate: value })
                  }
                  max={localSubscription.endDate}
                  className="max-w-[160px]"
                />
                <label className="text-chariot-text-primary shrink-0 text-xs">
                  End
                </label>
                <StyledDatePicker
                  label=""
                  name="end-date"
                  value={localSubscription.endDate || ''}
                  onChange={value =>
                    handleSubscriptionChange({ endDate: value })
                  }
                  min={localSubscription.startDate}
                  className="max-w-[160px]"
                />
              </div>
            </section>

            {/* Attack Surface Coverage Section */}
            <section className="space-y-4">
              <h3 className="text-chariot-text-primary text-lg font-semibold">
                Attack Surface Coverage
              </h3>
              <AttackSurfaceSelector
                variant="compact"
                value={{
                  external: localSubscription.attackSurface.external || false,
                  applications:
                    localSubscription.attackSurface.applications || false,
                  internal: localSubscription.attackSurface.internal || false,
                  cloud: localSubscription.attackSurface.cloud || false,
                  code: localSubscription.attackSurface.code || false,
                  users: localSubscription.attackSurface.users || false,
                  device: localSubscription.attackSurface.device || false,
                }}
                onChange={handleAttackSurfaceChange}
                onSelectionChange={handleAttackSurfaceSelectionChange}
                requiredOptions={['external']}
              />
            </section>
          </div>

          {/* Scanning Frequency + Subscription Tier Row */}
          <div className="grid grid-cols-1 gap-3">
            {/* Scanning Frequency Section */}
            <ScanningFrequencySelector
              selectedFrequency={
                localSubscription.scanSchedule as ScanFrequency
              }
              onFrequencyChange={frequency =>
                handleSubscriptionChange({ scanSchedule: frequency })
              }
            />

            {/* Subscription Tier Section */}
            <SubscriptionTierSelector
              selectedTier={localSubscription.selectedTier}
              onTierChange={tier => {
                // Professional and Ultimate tiers include web attack surface
                const isProfessionalOrUltimate =
                  tier === 'professional' || tier === 'ultimate';
                const updatedAttackSurface = isProfessionalOrUltimate
                  ? {
                      ...localSubscription.attackSurface,
                      applications: true,
                    }
                  : localSubscription.attackSurface;

                // Set default 50 FQDNs for Professional and Ultimate tiers
                // Only set if current value is 0 (don't override existing values)
                const updatedNumberOfApplications =
                  isProfessionalOrUltimate &&
                  localSubscription.numberOfApplications === 0
                    ? 50
                    : localSubscription.numberOfApplications;

                handleSubscriptionChange({
                  selectedTier: tier,
                  attackSurface: updatedAttackSurface,
                  numberOfApplications: updatedNumberOfApplications,
                });
              }}
              tiers={['freemium', 'foundational', 'professional', 'ultimate']}
            />
          </div>

          {/* Pricing Sections */}
          {isPraetorianUser ? (
            <>
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                {/* Usage Price Section */}
                <UsagePriceSection
                  actualTotalAssets={actualTotalAssets}
                  actualTotalApplications={actualTotalApplications}
                  usagePrice={usagePrice}
                  usageFloor={usageFloor}
                />

                {/* MSRP Price Section */}
                <MSRPPriceSection
                  localSubscription={localSubscription}
                  onAssetChange={value =>
                    setLocalSubscription(prev => ({
                      ...prev,
                      numberOfAssets: value,
                    }))
                  }
                  onAppChange={value =>
                    setLocalSubscription(prev => ({
                      ...prev,
                      numberOfApplications: value,
                    }))
                  }
                  msrp={msrp}
                  floor={floor}
                />
              </div>

              {/* Negotiated Price Section */}
              <NegotiatedPriceInput
                value={localSubscription.estimatedCost}
                onChange={value =>
                  handleSubscriptionChange({ estimatedCost: value })
                }
                msrp={msrp}
                usagePrice={usagePrice}
                floorPrice={floor}
              />
            </>
          ) : (
            /* Regular User - Single Estimated Price */
            <Container
              isSelected
              className="bg-chariot-background-primary flex items-center justify-between"
            >
              <h2
                id="price-section-title"
                className="text-chariot-text-primary text-sm font-medium"
              >
                Estimated Annual Price
              </h2>
              <div className="text-chariot-brand-primary text-2xl font-bold">
                <Input
                  name="estimatedCost"
                  className={cn(SUBSCRIPTION_INPUT_CLASS, 'text-right')}
                  value={localSubscription.estimatedCost.toString()}
                  onChange={e =>
                    handleSubscriptionChange({
                      estimatedCost: parseInt(e.target.value) || 0,
                    })
                  }
                  placeholder="Enter price"
                />
              </div>
            </Container>
          )}
        </div>
      </Loader>
    </Modal>
  );
}
